import { ReactElement, useCallback } from 'react'

import { useDispatch } from 'react-redux'

import {
    makeUpdateWidgetPagination,
    makeUpdateWidgetSorter,
    makeUpdateWidgetTableColumns,
} from 'actions/ui/shared/dashboard'
import type { Pagination, Sorter } from 'types'

import { TableWidgetBase } from './TableWidgetBase'
import type { TableWidgetProps } from './types'

function TableWidget({
    widget,
    pageNumber,
    onPageNumberChange,
    ...rest
}: TableWidgetProps): ReactElement {
    const dispatch = useDispatch()

    const handleColumnsChange = useCallback(
        (next: {
            metrics: TableWidgetProps['widget']['metrics']
            group_by: TableWidgetProps['widget']['group_by']
        }): void => {
            dispatch(
                makeUpdateWidgetTableColumns()({
                    widgetId: widget.id,
                    metrics: next.metrics,
                    group_by: next.group_by,
                })
            )
        },
        [dispatch, widget.id]
    )

    const handlePaginationChange = useCallback(
        (newPagination: Pagination): void => {
            dispatch(
                makeUpdateWidgetPagination()({
                    widgetId: widget.id,
                    pagination: newPagination,
                })
            )
        },
        [dispatch, widget.id]
    )

    const handleSorterChange = useCallback(
        (newSorter: Sorter): void => {
            dispatch(
                makeUpdateWidgetSorter()({
                    widgetId: widget.id,
                    sorter: {
                        ...newSorter,
                        field: (newSorter?.field as any)?.join('__'),
                    },
                })
            )
        },
        [dispatch, widget.id]
    )

    return (
        <TableWidgetBase
            {...rest}
            widget={widget}
            pageNumber={pageNumber}
            onPageNumberChange={onPageNumberChange}
            onColumnsChange={handleColumnsChange}
            onPaginationChange={handlePaginationChange}
            onSorterChange={handleSorterChange}
        />
    )
}

export default TableWidget
